import VideoResource from 'types/VideoResource';

// URL query-param keys; kept in the URL so filters survive refresh / deep-links.
export const TAGS_PARAM = 'tags';
export const TINY_PLUS_PARAM = 'tiny_plus_only';

// We surface only these pills, not the DB's full tag set. The "Tiny+ Exclusive"
// pill toggles the `is_tiny_plus_only` flag instead of matching a tag string.
export const CURATED_TAG_FILTERS = ['Baby', 'Adult', 'Vaginal'] as const;

export const TINY_PLUS_TAG_LABEL = 'Tiny+ Exclusive';

// Both the new label and the legacy DB value get the gold treatment during rollout.
export const TINY_PLUS_TAG_VARIANTS = new Set<string>([
  TINY_PLUS_TAG_LABEL,
  'Tiny+ Member Exclusive',
]);

export const UPSELL_SOURCE = 'resources_page' as const;

// Appended to the checkout URL to attribute conversions from this modal.
export const UPSELL_CHECKOUT_SOURCE = 'resources_upsell';

// Copy verbatim from Figma (node 2163:13020).
export const UPSELL_BENEFITS = [
  '2 gut or vaginal health tests annually',
  'A complementary 1-on-1 coaching session with a microbiome specialist annually',
  'Order additional tests for your whole family at a members-only rate of $169/kit',
  'Additional coaching sessions at $39/call',
  'Exclusive access to members-only virtual events and resources',
  'Unlimited text and email support throughout the year',
];

export interface ResourceFilters {
  tags: string[];
  tinyPlusOnly: boolean;
}

// Pulls the 11-char id from the various YouTube URL shapes (watch?v=, youtu.be/,
// /embed/, /v/).
const YOUTUBE_ID_REGEX =
  /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;

export const getYouTubeVideoId = (videoUrl?: string): string => {
  if (!videoUrl) return '';
  const match = videoUrl.match(YOUTUBE_ID_REGEX);
  return match ? match[1] : '';
};

export const getYouTubeThumbnail = (videoUrl?: string): string => {
  const videoId = getYouTubeVideoId(videoUrl);
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '';
};

// Tags are stored as a single comma-separated string on the resource row.
export const parseTags = (raw?: string): string[] =>
  (raw ?? '')
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);

// Shared by the visible-list memo and the analytics result-count so the two
// can't drift.
export const matchesFilters = (
  video: VideoResource,
  { tags, tinyPlusOnly }: ResourceFilters,
): boolean => {
  if (tinyPlusOnly && !video.is_tiny_plus_only) return false;
  if (tags.length) {
    const videoTags = parseTags(video.tags);
    if (!tags.some(tag => videoTags.includes(tag))) return false;
  }
  return true;
};

// Locked for the current viewer. Key off `is_tiny_plus_only`, never `video_url`:
// exclusive videos still carry a URL server-side for member playback.
export const isExclusiveLocked = (
  video: VideoResource,
  isAnyTinyPlus: boolean,
): boolean => !!video.is_tiny_plus_only && !isAnyTinyPlus;

interface GateArgs {
  hideUpsells?: boolean;
  isAnyTinyPlus: boolean;
}

// Whether Tiny+ exclusive content (videos, the filter pill, badges) must be
// removed for this viewer rather than previewed behind the upsell (ENG-5047).
// Members always see everything, whatever their portal config says.
export const shouldHideTinyPlusContent = ({
  hideUpsells,
  isAnyTinyPlus,
}: GateArgs): boolean => !!hideUpsells && !isAnyTinyPlus;

// Member/partner gating applied before the filter pills. A viewer whose
// portal config hides upsells never sees exclusive content — no locked
// preview, no modal — regardless of which config layer set the flag.
export const gateVideoResources = (
  videos: VideoResource[] | undefined,
  gate: GateArgs,
): VideoResource[] | undefined => {
  if (!videos) return undefined;
  if (shouldHideTinyPlusContent(gate)) {
    return videos.filter(video => !video.is_tiny_plus_only);
  }
  return videos;
};
